import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "is_cookie_consent_v1";

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    data-testid={testid}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${
      checked ? "bg-primary" : "bg-border"
    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-300 ${
        checked ? "translate-x-[22px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // UK GDPR / PECR: non-essential cookies must default to OFF (no pre-consent).
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
    // Allow users to withdraw/change consent from the footer link.
    const reopen = () => {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        setPrefs({
          necessary: true,
          analytics: !!saved.analytics,
          marketing: !!saved.marketing,
        });
      } catch {
        /* ignore */
      }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-preferences", reopen);
    return () => window.removeEventListener("open-cookie-preferences", reopen);
  }, []);

  const persist = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...choice, ts: Date.now() }));
    } catch {
      /* ignore */
    }
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cookie-consent"
          className="fixed inset-x-0 bottom-0 z-[200] px-4 pb-4 sm:px-6 sm:pb-6"
        >
          <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card/95 p-6 shadow-2xl backdrop-blur-xl sm:p-8">
            <div className="flex items-start gap-4">
              <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary sm:flex">
                <Cookie size={20} strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-medium tracking-tight text-foreground">
                  We value your privacy
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  We use cookies to improve your experience, analyse site traffic and support our
                  marketing. By clicking “Accept all” you agree to our use of cookies. Read our{" "}
                  <a href="/cookie-policy" className="text-primary underline underline-offset-2">
                    Cookie Policy
                  </a>{" "}
                  and{" "}
                  <a href="/privacy-policy" className="text-primary underline underline-offset-2">
                    Privacy Policy
                  </a>
                  .
                </p>

                <AnimatePresence initial={false}>
                  {showSettings && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 space-y-4 border-t border-border pt-5">
                        {[
                          {
                            key: "necessary",
                            title: "Strictly necessary",
                            desc: "Required for the site to function. Always on.",
                            disabled: true,
                          },
                          {
                            key: "analytics",
                            title: "Analytics",
                            desc: "Help us understand how visitors use the site.",
                          },
                          {
                            key: "marketing",
                            title: "Marketing",
                            desc: "Used to deliver relevant content and measure campaigns.",
                          },
                        ].map((c) => (
                          <div key={c.key} className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-sm font-medium text-foreground">{c.title}</p>
                              <p className="text-xs text-muted-foreground">{c.desc}</p>
                            </div>
                            <Toggle
                              testid={`cookie-toggle-${c.key}`}
                              checked={prefs[c.key]}
                              disabled={c.disabled}
                              onChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
                            />
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-300 hover:scale-[1.03]"
                  >
                    Accept all
                  </button>
                  <button
                    data-testid="cookie-reject-all"
                    onClick={rejectAll}
                    className="rounded-full border border-border px-6 py-2.5 text-sm text-foreground transition-colors duration-300 hover:border-foreground"
                  >
                    Reject all
                  </button>
                  {showSettings ? (
                    <button
                      data-testid="cookie-save"
                      onClick={savePrefs}
                      className="rounded-full border border-border px-6 py-2.5 text-sm text-foreground transition-colors duration-300 hover:border-foreground"
                    >
                      Save settings
                    </button>
                  ) : (
                    <button
                      data-testid="cookie-show-settings"
                      onClick={() => setShowSettings(true)}
                      className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground sm:ml-1"
                    >
                      Show settings
                    </button>
                  )}
                </div>
              </div>
              <button
                data-testid="cookie-close"
                onClick={rejectAll}
                aria-label="Dismiss"
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
